import {useState} from "react";
import {bool, func, any, objectOf} from "prop-types";
import {
    getMaxLengths,
    unitList,
    MAX_32_BIT_SIGNED_INTEGER,
    LETTER_ELEMENTS,
    OPERATORS,
    VALID_INPUT_REGULAR_EXPRESSIONS,
} from "./constants";
import IconButton from "../../../common/icon-button/icon-button";
import TextOverflowTooltip from "../../../common/TextOverflowTooltip";
import WidgetOptions from "../widget-options/widget-options";
import KeyPadButton from "./Keypad/KeypadButton";
import "./BasicCalculator.scss";
import {
    intToBase,
    convertToNon10Base,
    baseToIntConvertFunction,
} from "../../../../helpers/base-conversions";
import {add, multiply, subtract, divide} from "./operations";
import {error, info} from "../../../../helpers/notifications";
import WidgetHeader from "../widget-header/WidgetHeader";

const BasicCalculator = ({isEditAllowed, showWidgetInfo, onRemove, onClone, data}) => {
    const [calculatorData, setCalculatorData] = useState({
        currentResult: undefined,
        currentNumber: undefined,
        lastInputType: undefined,
        lastOperation: undefined,
        base: 10,
        memory: undefined,
        error: undefined,
        maxLength: 9,
    });

    const displayValue =
        calculatorData.currentNumber || calculatorData.currentResult || calculatorData.error;
    const isBinaryBase = calculatorData.base === 2;
    const isInvalidBaseForDigits_8_9 = calculatorData.base === 2 || calculatorData.base === 8;
    const isInvalidBaseForLetterElements = calculatorData.base !== 16;
    const isOperatorDisabled = !!calculatorData.error;
    const isToggleSignDisabled = !!calculatorData.error;
    const isEqualsDisabled = !!calculatorData.error;
    const isDotDisabled = calculatorData.base !== 10 || calculatorData.error;
    const isStoreMemoryDisabled =
        calculatorData.error || !calculatorData.currentNumber || calculatorData.memory;
    const isReadMemoryDisabled = calculatorData.error || !calculatorData.memory;

    const setCurrentNumber = (number) => {
        setCalculatorData((state) => {
            if (state.lastOperation === "=") {
                return {
                    ...state,
                    currentResult: undefined,
                    lastOperation: undefined,
                    currentNumber: number,
                    lastInputType: "number",
                    error: undefined,
                };
            }

            return {
                ...state,
                currentNumber:
                    state.currentNumber && state.currentNumber !== "0"
                        ? state.currentNumber.length < state.maxLength
                            ? `${state.currentNumber}${number}`
                            : state.currentNumber
                        : number,
                lastInputType: "number",
                error: undefined,
            };
        });
    };

    const setDot = () => {
        if (
            calculatorData.lastInputType === "operation" ||
            calculatorData.currentNumber?.toString().includes(".")
        ) {
            return;
        }

        setCalculatorData((state) => {
            let {currentNumber, maxLength} = state;

            if (calculatorData.lastOperation === "=") {
                return {
                    ...state,
                    currentResult: undefined,
                    lastOperation: undefined,
                    lastInputType: "number",
                    error: undefined,
                    currentNumber: "0.",
                };
            }

            if (!currentNumber) {
                return {
                    ...state,
                    currentNumber: "0.",
                };
            }
            currentNumber = currentNumber.toString();
            return {
                ...state,
                currentNumber:
                    currentNumber.length < maxLength ? `${currentNumber}.` : currentNumber,
                lastInputType: "number",
                error: undefined,
            };
        });
    };

    const clearAll = () => {
        setCalculatorData((state) => ({
            ...state,
            currentResult: undefined,
            currentNumber: undefined,
            lastOperation: undefined,
            lastInputType: undefined,
            error: undefined,
        }));
    };

    const clearChar = () => {
        if (calculatorData.error) {
            clearAll();
        }

        if (calculatorData.lastInputType === "operation" || calculatorData.lastOperation === "=") {
            return;
        }

        setCalculatorData((state) => {
            const {currentNumber} = state;

            return {
                ...state,
                currentNumber: currentNumber?.toString().slice(0, currentNumber.length - 1),
            };
        });
    };

    const getResult = ({currentResult, currentNumber, base, lastOperation}) => {
        if (!currentResult || !currentNumber) {
            return;
        }

        if (currentNumber.toString().endsWith(".")) {
            currentNumber = currentNumber.toString().slice(0, currentNumber.length - 1);
        }

        switch (lastOperation) {
            case "+":
                return add(currentResult, currentNumber, base);
            case "-":
                return subtract(currentResult, currentNumber, base);
            case "*":
                return multiply(currentResult, currentNumber, base);
            case "/":
                return Number(currentNumber) !== 0
                    ? divide(currentResult, currentNumber, base)
                    : "Cannot divide by zero";
            default:
                return;
        }
    };

    const setResult = () => {
        let result = getResult(calculatorData);
        if (!result) {
            return;
        }

        setCalculatorData((state) => {
            if (result === "Cannot divide by zero") {
                return {
                    ...state,
                    currentResult: undefined,
                    currentNumber: undefined,
                    error: "Cannot divide by zero",
                };
            }

            return {
                ...state,
                currentResult: undefined,
                // lastInput: undefined,
                lastOperation: "=",
                currentNumber: result,
            };
        });
    };

    const operate = (operation) => {
        setCalculatorData((state) => {
            const {currentNumber, lastInputType} = state;

            const newState = {
                ...state,
                currentNumber: undefined,
                lastOperation: operation,
                lastInputType: "operation",
            };

            if (!currentNumber) {
                return newState;
            }

            let result = getResult(state);

            if (result === "Cannot divide by zero") {
                return {
                    ...state,
                    currentResult: undefined,
                    currentNumber: undefined,
                    error: "Cannot divide by zero",
                };
            }

            if (!result) {
                return {
                    ...newState,
                    currentResult: currentNumber,
                };
            }

            return lastInputType === "operation"
                ? newState
                : {
                      ...newState,
                      currentResult: result,
                  };
        });
    };

    const toggleSign = () => {
        if (!calculatorData.currentNumber || calculatorData.currentNumber === "0") {
            return;
        }

        setCalculatorData((state) => {
            const complement = ~parseInt(state.currentNumber, state.base) + 1;
            return {
                ...state,
                currentNumber:
                    state.base === 10
                        ? (-state.currentNumber).toString()
                        : intToBase(complement, state.base),
            };
        });
    };

    const setMemory = () => {
        setCalculatorData((state) => ({
            ...state,
            memory: state.currentNumber,
        }));
    };

    const readMemory = () => {
        setCalculatorData((state) => ({
            ...state,
            currentNumber: state.memory,
            memory: undefined,
        }));
    };

    const setBase = (base) => {
        if (calculatorData.base === base) {
            return;
        }

        setCalculatorData((state) => {
            const {currentResult, currentNumber, base: currBase} = state;
            const maxLength = getMaxLengths()[base];

            const newState = {
                ...state,
                memory: undefined,
                base,
                maxLength,
            };

            if (base !== 10 && currBase !== 10) {
                const newCurrResult = convertToNon10Base(currentResult, currBase, base);
                const newCurrNumber = convertToNon10Base(currentNumber, currBase, base);

                return {
                    ...newState,
                    currentResult: newCurrResult,
                    currentNumber: newCurrNumber,
                };
            }

            if (currBase === 10) {
                if (
                    currentResult?.toString().includes(".") ||
                    currentNumber?.toString().includes(".")
                ) {
                    return {
                        ...newState,
                        currentResult: "0",
                        currentNumber: "0",
                    };
                }

                if (
                    currentNumber > MAX_32_BIT_SIGNED_INTEGER ||
                    currentResult > MAX_32_BIT_SIGNED_INTEGER
                ) {
                    return {
                        ...newState,
                        currentResult: undefined,
                        currentNumber: undefined,
                        error: "Maximum positive value for a 32-bit signed integer (2147483647) is exceeded",
                    };
                }

                const newCurrResult = intToBase(currentResult, base);
                const newCurrNumber = intToBase(currentNumber, base);

                return {
                    ...newState,
                    currentResult: newCurrResult,
                    currentNumber: newCurrNumber,
                };
            }

            const newCurrResult = baseToIntConvertFunction[currBase](currentResult)?.toString();
            const newCurrNumber = baseToIntConvertFunction[currBase](currentNumber)?.toString();

            return {
                ...newState,
                currentResult: newCurrResult,
                currentNumber: newCurrNumber,
            };
        });
    };

    const onCopyTextToClipboard = async (newClip) => {
        try {
            await navigator.clipboard.writeText(newClip);
            info({
                message: "Value copied to clipboard.",
            });
        } catch (err) {
            error({
                message: "An error occurred while copying the value.",
            });
        }
    };

    const isValueTooLong = (value) => {
        //if negative decimal number add 1 for - sigh
        if (calculatorData.base === 10 && value < 0) {
            return value.length > calculatorData.maxLength + 1;
        }
        return value.length > calculatorData.maxLength;
    };

    const removeExtraStartingZeroes = (value) => {
        if (!isNaN(value)) {
            return (+value).toString();
        }
        value = value.replace(/^0*/, "");
        return value;
    };

    const isCMDKey = (e) => {
        if (!e.metaKey) {
            return false;
        }

        if (navigator.userAgentData?.platform.toUpperCase().indexOf("MAC") > -1) {
            return true;
        } else if (navigator.userAgent.toUpperCase().indexOf("MAC") > -1) {
            return true;
        }
        return false;
    };

    const onKeyDownHandler = async (e) => {
        //Tab
        if (e.key === "Tab") {
            e.preventDefault();
            return;
        }

        //Space
        if (e.key === " ") {
            e.preventDefault();
            return;
        }

        //numbers
        if (e.key === "1" || e.key === "0") {
            setCurrentNumber(e.key);
            return;
        }

        if ((e.key === "8" || e.key === "9") && !isInvalidBaseForDigits_8_9) {
            setCurrentNumber(e.key);
            return;
        }

        if (!isNaN(e.key) && +e.key < 8 && !isBinaryBase) {
            setCurrentNumber(e.key);
            return;
        }

        //hex letter elements
        if (LETTER_ELEMENTS.indexOf(e.key) > -1 && !isInvalidBaseForLetterElements) {
            setCurrentNumber(e.key);
            return;
        }

        //operators
        if (OPERATORS.indexOf(e.key) > -1 && !isOperatorDisabled) {
            operate(e.key);
            return;
        }

        //equals - Enter
        if (e.key === "Enter" && !isEqualsDisabled) {
            e.preventDefault();
            setResult();
            return;
        }

        //C - Delete
        if (e.key === "Delete") {
            clearAll();
            return;
        }

        //Backspace
        if (e.key === "Backspace") {
            clearChar();
            return;
        }

        //Change sign(+/-) - F9
        if (e.key === "F9" && !isToggleSignDisabled) {
            toggleSign();
            return;
        }

        //.
        if (e.key === "." && !isDotDisabled) {
            setDot();
            return;
        }

        //Store memory - ctrl/cmd+M
        if ((e.ctrlKey || isCMDKey(e)) && e.key.toUpperCase() === "M" && !isStoreMemoryDisabled) {
            setMemory();
        }

        //Read memory - ctrl/cmd+R
        if ((e.ctrlKey || isCMDKey(e)) && e.key.toUpperCase() === "R") {
            e.preventDefault();
            if (!isReadMemoryDisabled) {
                readMemory();
            }
        }

        //Copy - ctrl/cmd+C
        if ((e.ctrlKey || isCMDKey(e)) && e.key.toUpperCase() === "C") {
            await onCopyTextToClipboard(displayValue);
        }

        //Paste - ctrl/cmd+V
        if (
            (e.ctrlKey || isCMDKey(e)) &&
            e.key.toUpperCase() === "V" &&
            navigator.clipboard.readText
        ) {
            let input = await navigator.clipboard.readText();
            input = removeExtraStartingZeroes(input);

            if (
                VALID_INPUT_REGULAR_EXPRESSIONS[calculatorData.base].test(input) &&
                !isValueTooLong(input)
            ) {
                setCalculatorData((state) => ({
                    ...state,
                    currentNumber: input,
                    lastInputType: "number",
                    error: undefined,
                }));
            } else {
                setCalculatorData((state) => ({
                    ...state,
                    currentResult: undefined,
                    currentNumber: undefined,
                    error: "Invalid input",
                }));
            }
        }
    };

    return (
        <div
            className="glu-dashboard-card glu-content calculator-outline"
            onKeyDown={onKeyDownHandler}
            tabIndex={0}
        >
            <WidgetHeader
                isEditAllowed={isEditAllowed}
                title={data.title}
                height={480}
                width={500}
                showWidgetInfo={showWidgetInfo(data)}
                onRemove={onRemove(data)}
                onClone={onClone(data)}
            />
            <div className="glu-content__body">
                <div className="glu-content__section glu-content__separator glu-content__separator--inner glu-spread-v glu-overflow-auto">
                    <div className="calculator-container">
                        <div className={`result-container ${calculatorData.error ? "error" : ""}`}>
                            <div className="result" data-testid="result">
                                {displayValue}
                            </div>
                        </div>

                        <div className="units-keypad-container">
                            <div className="units-switch-container">
                                {unitList.map((unit) => (
                                    <div
                                        className={
                                            unit.base === calculatorData.base
                                                ? "unit-active"
                                                : "unit"
                                        }
                                        key={unit.id}
                                        onClick={() => setBase(unit.base)}
                                    >
                                        {unit.label}
                                    </div>
                                ))}
                            </div>
                            <div className="keypad-container">
                                <div className="letter-container">
                                    {LETTER_ELEMENTS.map((item) => (
                                        <KeyPadButton
                                            disabled={isInvalidBaseForLetterElements}
                                            label={item}
                                            value={item}
                                            customClass="letter-element"
                                            key={item}
                                            testId={item}
                                            onClick={() => setCurrentNumber(item)}
                                        />
                                    ))}
                                </div>
                                <div className="keypads">
                                    {!calculatorData.memory ? (
                                        <KeyPadButton
                                            label="M"
                                            value="M"
                                            customClass="keypad-blue-element"
                                            onClick={setMemory}
                                            disabled={isStoreMemoryDisabled}
                                        />
                                    ) : (
                                        <KeyPadButton
                                            label="Mr"
                                            value="Mr"
                                            customClass="keypad-blue-element"
                                            onClick={readMemory}
                                            disabled={isReadMemoryDisabled}
                                        />
                                    )}
                                    <KeyPadButton
                                        label="C"
                                        value="clear"
                                        customClass="keypad-red-element"
                                        onClick={clearAll}
                                    />
                                    <KeyPadButton
                                        icon="clear-char"
                                        value="delete"
                                        testId="delete"
                                        customClass="keypad-element"
                                        onClick={clearChar}
                                    />

                                    <KeyPadButton
                                        icon="multiply"
                                        testId="multiply"
                                        value="*"
                                        customClass="keypad-blue-element"
                                        onClick={() => operate("*")}
                                        disabled={isOperatorDisabled}
                                    />
                                    <KeyPadButton
                                        onClick={() => setCurrentNumber("7")}
                                        label="7"
                                        value="7"
                                        customClass="keypad-element"
                                        disabled={isBinaryBase}
                                    />
                                    <KeyPadButton
                                        onClick={() => setCurrentNumber("8")}
                                        label="8"
                                        value="8"
                                        customClass="keypad-element"
                                        disabled={isInvalidBaseForDigits_8_9}
                                    />
                                    <KeyPadButton
                                        onClick={() => setCurrentNumber("9")}
                                        label="9"
                                        value="9"
                                        customClass="keypad-element"
                                        disabled={isInvalidBaseForDigits_8_9}
                                    />

                                    <KeyPadButton
                                        icon="divide"
                                        testId="divide"
                                        value="/"
                                        customClass="keypad-blue-element"
                                        onClick={() => operate("/")}
                                        disabled={isOperatorDisabled}
                                    />

                                    <KeyPadButton
                                        onClick={() => setCurrentNumber("4")}
                                        label="4"
                                        value="4"
                                        customClass="keypad-element"
                                        disabled={isBinaryBase}
                                    />
                                    <KeyPadButton
                                        onClick={() => setCurrentNumber("5")}
                                        label="5"
                                        value="5"
                                        customClass="keypad-element"
                                        disabled={isBinaryBase}
                                    />
                                    <KeyPadButton
                                        onClick={() => setCurrentNumber("6")}
                                        label="6"
                                        value="6"
                                        customClass="keypad-element"
                                        disabled={isBinaryBase}
                                    />

                                    <KeyPadButton
                                        icon="subtract"
                                        testId="minus"
                                        value="-"
                                        customClass="keypad-blue-element"
                                        onClick={() => operate("-")}
                                        disabled={isOperatorDisabled}
                                    />
                                    <KeyPadButton
                                        onClick={() => setCurrentNumber("1")}
                                        label="1"
                                        value="1"
                                        customClass="keypad-element"
                                    />
                                    <KeyPadButton
                                        onClick={() => setCurrentNumber("2")}
                                        label="2"
                                        value="2"
                                        customClass="keypad-element"
                                        disabled={isBinaryBase}
                                    />
                                    <KeyPadButton
                                        onClick={() => setCurrentNumber("3")}
                                        label="3"
                                        value="3"
                                        customClass="keypad-element"
                                        disabled={isBinaryBase}
                                    />

                                    <KeyPadButton
                                        icon="add"
                                        testId="plus"
                                        value="plus"
                                        customClass="keypad-blue-element"
                                        onClick={() => operate("+")}
                                        disabled={isOperatorDisabled}
                                    />
                                    <KeyPadButton
                                        label="+/-"
                                        value="plus / minus"
                                        customClass="keypad-element"
                                        onClick={toggleSign}
                                        disabled={isToggleSignDisabled}
                                    />
                                    <KeyPadButton
                                        label="0"
                                        value="null"
                                        customClass="keypad-element"
                                        onClick={() => setCurrentNumber("0")}
                                    />
                                    <KeyPadButton
                                        label="."
                                        value="."
                                        customClass="keypad-element"
                                        onClick={setDot}
                                        disabled={isDotDisabled}
                                    />
                                    <KeyPadButton
                                        icon="equals"
                                        testId="equals"
                                        value="="
                                        customClass="keypad-element-equal"
                                        onClick={setResult}
                                        disabled={isEqualsDisabled}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

BasicCalculator.propTypes = {
    isEditAllowed: bool.isRequired,
    showWidgetInfo: func.isRequired,
    onRemove: func.isRequired,
    onClone: func.isRequired,
    data: objectOf(any).isRequired,
};

export default BasicCalculator;
